import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";

const FAQS = [
 {
 q: "Does copying a solicitor into an email make it privileged?",
 a: "No. The dominant purpose of the communication still has to be giving or obtaining legal advice. Copying a lawyer into an ordinary HR discussion does not change what the email is for.",
 },
 {
 q: "Does writing privileged and confidential on a document make it privileged?",
 a: "No. The label does not create the protection. Privilege depends on who the communication is between, whether it is confidential, and its dominant purpose.",
 },
 {
 q: "Does forwarding a document to a lawyer make the original privileged?",
 a: "No. Forwarding a pre-existing document after the event does not make the original privileged. The earlier document can still be disclosable.",
 },
 {
 q: "Is without prejudice the same as legal privilege?",
 a: "No. Without prejudice protection applies to genuine settlement negotiations about an existing dispute. Legal privilege covers confidential legal advice and, in narrower circumstances, communications made for the conduct of contemplated litigation.",
 },
];

export default function LegalPrivilege() {
 const origin = window.location.origin;
 const pageUrl = `${origin}/legal-privilege-and-hr-advice`;

 return (
 <main data-testid="legal-privilege-page">
 <Seo
 title="Is HR Advice Legally Privileged? | Electra HR"
 siteName="Electra HR"
 description="Usually not. Learn what legal advice privilege and litigation privilege protect, what does not create privilege, and when HR matters should move to Bonsai Law."
 image="/og/default.jpg"
 jsonLd={{
 "@context": "https://schema.org",
 "@graph": [
 {
 "@type": "WebPage",
 "@id": pageUrl,
 url: pageUrl,
 name: "Is your HR advice legally privileged?",
 description: "A plain-English guide to legal advice privilege, litigation privilege and without prejudice protection in HR matters.",
 isPartOf: { "@id": `${origin}/#website` },
 publisher: { "@id": `${origin}/#organization` },
 datePublished: "2026-09-07",
 dateModified: "2026-09-07",
 reviewedBy: {
 "@type": "Person",
 name: "Vanessa Challess",
 jobTitle: "Solicitor",
 },
 },
 {
 "@type": "FAQPage",
 mainEntity: FAQS.map((f) => ({
 "@type": "Question",
 name: f.q,
 acceptedAnswer: { "@type": "Answer", text: f.a },
 })),
 },
 {
 "@type": "BreadcrumbList",
 itemListElement: [
 { "@type": "ListItem", position: 1, name: "Home", item: origin },
 { "@type": "ListItem", position: 2, name: "Legal privilege and HR advice", item: pageUrl },
 ],
 },
 ],
 }}
 />

 <section className="grain bg-ink pb-16 pt-32 sm:pt-40 text-paper">
 <div className="container-x max-w-4xl">
 <p className="eyebrow text-pink">Legal privilege, plainly</p>
 <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
 Is your HR advice legally privileged?
 </h1>
 <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-paper/70">
 Usually not. Legal advice privilege protects confidential communications between a client and a lawyer where the dominant purpose is giving or obtaining legal advice. Advice from an HR consultant who is not acting as your lawyer falls outside it.
 </p>
 <p className="mt-6 font-mono text-xs uppercase tracking-[0.18em] text-paper/45">
 Reviewed by Vanessa Challess, solicitor · 6 September 2026
 </p>
 </div>
 </section>

 <section className="bg-paper py-16 sm:py-24">
 <div className="container-x max-w-3xl">
 <Reveal>
 <div className="border-l-4 border-pink bg-pink/10 p-6">
 <p className="text-base font-medium leading-relaxed text-ink/80">
 Copying a solicitor in, writing privileged on the document, or forwarding it to a lawyer afterwards does not create privilege. The protection depends on the communication, its purpose and when the legal risk is recognised.
 </p>
 </div>
 </Reveal>

 <div className="mt-12 space-y-12">
 <section>
 <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">What legal advice privilege actually is</h2>
 <p className="mt-4 text-base leading-relaxed text-ink/70">
 Legal advice privilege has four basic requirements. There must be a communication, it must be between client and lawyer, it must be made in confidence, and its dominant purpose must be giving or obtaining legal advice.
 </p>
 <p className="mt-4 text-base leading-relaxed text-ink/70">
 The Court of Appeal confirmed the dominant purpose test for legal advice privilege in <em>Jet2.com v Civil Aviation Authority</em> in 2020. That matters because a communication is not protected just because a lawyer appears somewhere in the chain.
 </p>
 </section>

 <section>
 <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Three things that do not create privilege</h2>
 <div className="mt-6 space-y-4">
 {[
 ["Copying a solicitor into an email", "If the email is really about managing an employee, copying in a lawyer does not turn it into a request for legal advice."],
 ["Writing privileged and confidential at the top", "The label does not decide the legal test. The purpose and context of the communication do."],
 ["Forwarding a document to a lawyer after the fact", "The lawyer may advise on the document, but the forwarding does not make the original document privileged."],
 ].map(([title, text]) => (
 <div key={title} className="flex gap-4 border border-ink/15 bg-paper p-5">
 <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-pink" />
 <div>
 <h3 className="font-display text-lg font-semibold">{title}</h3>
 <p className="mt-1 text-sm sm:text-base leading-relaxed text-ink/70">{text}</p>
 </div>
 </div>
 ))}
 </div>
 </section>

 <section>
 <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">The other kind of privilege, and why timing is everything</h2>
 <p className="mt-4 text-base leading-relaxed text-ink/70">
 Litigation privilege is separate from legal advice privilege and works differently. It can cover communications with people who are not lawyers, including HR consultants, investigators and experts, but only where litigation is in reasonable contemplation and the dominant purpose of the communication is conducting that litigation.
 </p>
 <p className="mt-4 text-base leading-relaxed text-ink/70">
 Routine HR advice about a difficult employee will not usually meet that test because no litigation is yet in reasonable contemplation. A grievance investigation begun before anyone is contemplating a claim may not meet it either. By the time litigation clearly is contemplated, the damaging notes and emails have often already been written.
 </p>
 <p className="mt-4 text-base leading-relaxed text-ink/70">
 The practical protection therefore turns on recognising, correctly and early, when a people problem has become a legal matter.
 </p>
 </section>

 <section>
 <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Why this matters when a people problem goes wrong</h2>
 <p className="mt-4 text-base leading-relaxed text-ink/70">
 Notes, emails and investigation records created during a grievance or dismissal process can end up disclosable in a tribunal claim. Since 1 October 2026, the time limit for most tribunal claims has been six months less one day, instead of three months less one day. That widens the window in which those records can be called for.
 </p>
 <p className="mt-4 text-base leading-relaxed text-ink/70">
 Read the <Link to="/blog/tribunal-time-limits-double-1-october-2026" className="font-semibold underline decoration-pink decoration-2 underline-offset-4">tribunal time limits briefing</Link>, our <Link to="/blog/employment-rights-act-2025-what-do-employers-need-to-do" className="font-semibold underline decoration-pink decoration-2 underline-offset-4">Employment Rights Act 2025 pillar</Link>, and the <Link to="/services/grievances-disciplinaries" className="font-semibold underline decoration-pink decoration-2 underline-offset-4">grievances and disciplinaries service</Link> together if a live process is already under way.
 </p>
 </section>

 <section>
 <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Without prejudice is not the same thing</h2>
 <p className="mt-4 text-base leading-relaxed text-ink/70">
 Without prejudice protection can keep genuine settlement negotiations about an existing dispute from being put before a tribunal. It is not legal privilege, it does not apply just because the words are written at the top, there must be a genuine dispute, and the protection can be lost where there is unambiguous impropriety.
 </p>
 <p className="mt-4 text-base leading-relaxed text-ink/70">
 Ordinary unfair dismissal claims also have a separate statutory protection for pre-termination negotiations. The labels overlap in everyday conversation, but they are not interchangeable.
 </p>
 </section>

 <section>
 <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Where Electra HR sits, and where Bonsai Law takes over</h2>
 <p className="mt-4 text-base leading-relaxed text-ink/70">
 Electra HR is an HR consultancy, not a law firm, and is not SRA-regulated. Advice from Electra HR is not itself covered by legal advice privilege. What Electra HR provides is the judgement to recognise when a matter needs a lawyer, and a direct escalation route into Bonsai Law, a separate SRA-regulated firm, where a lawyer-client relationship and privilege can properly exist.
 </p>
 <p className="mt-4 text-base leading-relaxed text-ink/70">
 That route matters when a contemplated dismissal may be litigated, a discrimination allegation has been raised, or an investigation is likely to end up in a tribunal bundle. The aim is not to manufacture privilege after the event. It is to recognise the legal point before the paper trail is created.
 </p>
 </section>

 <section>
 <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">What to do if you think a matter is heading that way</h2>
 <ul className="mt-5 space-y-3 text-base leading-relaxed text-ink/70">
 {[
 "Keep records factual, dated and contemporaneous",
 "Assume nothing is protected unless you have had advice that it is",
 "Do not copy lawyers into HR emails as a cosmetic exercise",
 "Get a view on whether the matter needs legal advice before the damaging paper trail exists",
 ].map((item) => (
 <li key={item} className="flex gap-3">
 <CheckCircle2 size={18} className="mt-1 shrink-0 text-pink" />
 <span>{item}</span>
 </li>
 ))}
 </ul>
 <div className="mt-8 border-2 border-ink bg-ink p-7 text-paper">
 <p className="font-display text-xl font-semibold">Not sure whether this is still an HR issue?</p>
 <p className="mt-2 text-sm leading-relaxed text-paper/65">That is the moment to ask. We will tell you whether it is an HR process, a legal matter, or both.</p>
 <Link to="/contact" data-testid="privilege-contact-cta" className="group mt-5 inline-flex items-center gap-2 bg-pink px-6 py-3 font-bold text-ink transition-transform duration-300 hover:-translate-y-0.5">
 Talk to us <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
 </Link>
 </div>
 </section>

 <section>
 <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Privilege questions employers ask</h2>
 <div className="mt-6 divide-y divide-ink/10 border-y border-ink/10">
 {FAQS.map((f, i) => (
 <details key={f.q} className="group py-5" data-testid={`privilege-faq-${i}`}>
 <summary className="cursor-pointer list-none font-display text-lg font-semibold marker:hidden">
 {f.q}
 </summary>
 <p className="mt-3 text-sm sm:text-base leading-relaxed text-ink/70">{f.a}</p>
 </details>
 ))}
 </div>
 <p className="mt-5 text-sm leading-relaxed text-ink/60">
 More answers are on the <Link to="/faqs" className="font-semibold underline decoration-pink decoration-2 underline-offset-4">Electra HR FAQs page</Link>.
 </p>
 </section>
 </div>
 </div>
 </section>
 </main>
 );
}
